import React, { useCallback, useEffect, useRef, useState } from "react";
import _, { over, set } from 'lodash';
import cx from 'classnames';

import { AssetCard, PlaceholderAssetCard } from "../card";
import DraggableGrid, { DraggableGridHandle } from 'ruuri';

import { AssetLayoutProps } from "../../../types";
import Button from "../../../../../components/button";

import styles from './grid.module.scss';

interface HoverState {
  section: number;
  asset: string;
  position: {
    left: number;
    top: number;
  };
}

const GridLayout: React.FC<AssetLayoutProps> = ({ loading, assets, menuState, onMenuChange, onClick, onRemove, onMove, onMoveToSection, collection, section}) => {
  const gridRef = React.useRef<DraggableGridHandle | null>(null);

  const editing = useRef(menuState.primaryAction === 'edit');
  const showPlaceholder = loading && _.size(assets) === 0;
  const empty = !loading && _.size(assets) === 0;

  const [hoverOverSection, setHoverOverSection] = useState<HoverState | null>(null);

  const [assetsOrder, setAssetsOrder] = React.useState<string[]>([]);
  
  useEffect(() => {
    editing.current = menuState.primaryAction === 'edit';

    const order = assets.map(item => item.id);
    setAssetsOrder(order);
  }, [menuState.primaryAction, assets]);
  
  useEffect(() => {
    if (!editing.current && assetsOrder.length > 0) {
      gridRef.current?.grid?.sort(function (itemA, itemB) {
        const a1 = itemA.getElement()?.getAttribute('data-ruuri-id');
        const i1 = assetsOrder.findIndex(item => {
          return item == a1
        });

        const a2 = itemB.getElement()?.getAttribute('data-ruuri-id');
        const i2 = assetsOrder.findIndex(item => {
          return item == a2
        });

        return i1 - i2;
      });

      setAssetsOrder([]); 
    }

    synchronizeGridState();
    gridRef.current?.grid?.synchronize();
    gridRef.current?.grid?.layout(true);
}, [assetsOrder]);


  useEffect(() => {
    synchronizeGridState();
  }, [assets]);

  const synchronizeGridState = () => {
    const gridItems = gridRef.current?.grid?.getItems() || [];
    const missingItems = gridItems.filter(item => {
      const itemId = item.getElement()?.getAttribute('data-ruuri-id');

      return !assets.find(asset => asset.id == itemId);
    });

    if (_.size(missingItems) > 0) {
      gridRef.current?.grid?.remove(missingItems);
    }

    //gridRef.current?.grid?.add(existingItems);
    gridRef.current?.grid?.synchronize();
    gridRef.current?.grid?.layout(true);
  }

  const handleDimensionsChange = _.debounce(() => {
    gridRef.current?.grid?.refreshItems();
    gridRef.current?.grid?.layout(true);
  }, 300);

  const handleAssetRemove = (asset) => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  const onDragStart = useCallback(() => {
    return editing.current;
  }, [editing.current]);

  const onDragMove = (item, event) => {
    const element = item.getElement();
    if (!element) {
      return;
    }

    const { section, over } = findSectionIndex(event);
    const asset = item.getElement()?.getAttribute('data-ruuri-id');
    const rect = element.getBoundingClientRect();

    if (section >= 0) {
      setHoverOverSection({
        section: section,
        asset: asset,
        position: {
          left: event.clientX - rect.left,
          top: event.clientY - rect.top
        }
      });
    } else if (over) {
      setHoverOverSection({
        section: -1,
        asset: asset,
        position: {
          left: event.clientX - rect.left,
          top: event.clientY - rect.top
        }
      });
    } else {
      setHoverOverSection(null);
    }
  };

  const onDragEnd = (item, event) => {
    const element = item.getElement();
    if (!element) {
      return;
    }
    const { section } = findSectionIndex(event);
    const asset = item.getElement()?.getAttribute('data-ruuri-id');

    if(section >= 0 && asset) {
      const assetItem = assets.find(item => item.id == asset);
      if(assetItem){
        onMoveToSection(section, assetItem);
      }
    }

    setHoverOverSection(null);
    gridRef.current?.grid?.layout(true);
  }

  const findSectionIndex = (event) => {
    const elements = document.elementsFromPoint(event.clientX, event.clientY);

    if (elements == null) {
      return {
        section: -1,
        over: false
      };
    }

    const sectionNode = elements.find(element => {
      if (element.classList.contains('collection_section')) {
        return element;
      }

      return null;
    });

    const sectionBar = elements.find(element => {
      if (element.classList.contains('collection_sections')) {
        return element;
      }

      return null;
    });

    if (sectionNode) {
      const index = sectionNode.getAttribute('data-section-index');
      return {
        section: index ? parseInt(index) : -1,
        over: sectionBar != null
      }
    }

    return {
      section: -1,
      over: sectionBar != null
    };
  }

  return (
    <div className={styles.grid}>
      {showPlaceholder &&
        <div className={styles.placeholders}>
          <PlaceholderAssetCard height={400} />
          <PlaceholderAssetCard height={200} />
          <PlaceholderAssetCard height={500} />
          <PlaceholderAssetCard height={250} />
        </div>
      }

      {empty && <EmptyAssets onMenuChange={onMenuChange} />}

      {!showPlaceholder &&
        <DraggableGrid
          ref={gridRef}
          data={assets}
          uniKey={'id'}
          itemClass={styles.item}
          itemDraggingClass={styles.dragging}
          renderItem={itemData => {
            if (hoverOverSection && hoverOverSection.asset == itemData.id) {
              const section = hoverOverSection.section;

              return (
                <div className={cx(styles.dragOverSection, {
                  [styles.disabled]: section < 0
                })} style={hoverOverSection.position}>
                  Add to section
                </div>
              );
            }

            return (
              <AssetCard
                asset={itemData}
                editable={menuState.primaryAction === 'edit'}
                onDimensionsChange={handleDimensionsChange}
                onRemove={handleAssetRemove}
                onClick={onClick}
                collection={collection}
                sectionIndex={section}
              />
            );
          }}
          layout={{
            fillGaps: false,
            horizontal: false,
            alignRight: false,
            alignBottom: false,
            rounding: false
          }}
          showDuration={0}
          sortData={null}
          dragEnabled={true}
          dragSort={true}
          dragStartPredicate={onDragStart}
          onMove={(data) => {
            const element = data.item.getElement();
            if (!element) {
              return;
            }

            onMove(data.fromIndex, data.toIndex);
          }}
          onDragMove={onDragMove}
          onDragEnd={onDragEnd}
        />
      }
    </div>
  );
}

interface EmptyAssetsProps {
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (state: any) => void;
}

const EmptyAssets: React.FC<EmptyAssetsProps> = ({ onMenuChange }) => {
  const handleAddAsset = () => {
    onMenuChange({
      primaryAction: 'edit',
      secondaryAction: 'add-asset',
    });
  }

  return (
    <div className={styles.empty}>
      <div className={styles.card}>
        <img src="/images/collections/empty.svg" />
      </div>
      <div className={cx(styles.card, styles.message)}>
        <p>Time to build out your collection, asset by asset, section by section...</p>
        <Button variant={'primary'} icon="fa-solid fa-pencil" text="Edit" onClick={handleAddAsset} />
      </div>
    </div>
  );
}

export { GridLayout };